import { Button, Divider, Link, useDisclosure } from "@heroui/react";
import {
  FaBuilding,
  FaCity,
  FaMapPin,
  FaPenToSquare,
  FaPlus,
  FaRuler,
  FaSquare,
  FaTag,
  FaTrainSubway,
} from "react-icons/fa6";
import { useAppSelector } from "@/hooks/useAppSelector.ts";
import { useCallback, useEffect, useState } from "react";
import { BusinessProfile } from "@/api/businessProfiles";
import { api } from "@/api";
import { toast } from "react-toastify";
import { EditBusinessProfileModal } from "@/app/components/modals/businessProfile/editBusinessProfileModal";
import { OfficeLocation } from "@/api/locations";
import { CreateLocationModal } from "@/app/components/modals/location/createLocationModal";
import useErrorHandling from "@/hooks/useErrorHandling";
import {
  formatCurrency,
  getCurrentMonth,
  getMonthInRussian,
  sortAlphabeticallyByKey,
} from "@/utils";
import { PlanLimitsCompact } from "@/app/components/plans/PlanLimitsCompact";
import { useTour } from "@/hooks/useTour";
import {
  businessProfileLinks,
  Links,
} from "@/app/components/navigation/links/Links";

export function DashboardDemo() {
  const [businessProfileData, setBusinessProfileData] =
    useState<BusinessProfile | null>(null);
  const [locations, setLocations] = useState<OfficeLocation[] | null>(null);
  const [summaryLocations, setSummaryLocations] = useState<
    { locationId: number; pricePerSqm: number; totalPrice: number }[]
  >([]);

  const offices = useAppSelector((state) => state.handbooks.office);

  const handleError = useErrorHandling();

  const businessProfile = useAppSelector(
    (state) => state.businessProfile.businessProfile,
  );

  const editBusinessProfileModalDisclosure = useDisclosure();
  const createLocationModalDisclosure = useDisclosure();

  // Инициализация тура для главной страницы
  useTour(0);

  const loadData = useCallback(() => {
    if (businessProfile.id == 0) return;

    api.businessProfiles
      .get(businessProfile.id)
      .then((response) => {
        setBusinessProfileData(response.data);
      })
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
        localStorage.removeItem("businessProfileId");
      });

    api.businessProfiles
      .getLocations(businessProfile.id)
      .then((response) => {
        const sortedData = sortAlphabeticallyByKey(
          response.data,
          "display_name",
        );
        setLocations(sortedData);

        response.data.forEach((location) => {
          api.analytics
            .summary(
              getMonthInRussian(getCurrentMonth()),
              new Date().getFullYear().toString(),
              location.id,
            )
            .then((rls) => {
              const totalPrice = rls.data.rent_plan.reduce(
                (total, item) => total + (item.price ?? 0),
                0,
              );

              setSummaryLocations((value) => [
                ...value,
                {
                  locationId: location.id,
                  totalPrice,
                  pricePerSqm: totalPrice / location.total_area,
                },
              ]);
            })
            .catch((err) => {
              console.log(err);
            });
        });
      })
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [businessProfile, handleError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <>
      <CreateLocationModal
        disclosure={createLocationModalDisclosure}
        onLocationCreated={loadData}
      />
      <EditBusinessProfileModal
        disclosure={editBusinessProfileModalDisclosure}
        businessProfile={businessProfileData}
      />
      <div className="flex flex-col md:flex-row gap-4 w-full">
        <Links links={businessProfileLinks("/dashboard")} />
        <div className="flex-grow flex flex-col gap-2">
          <div className="w-full flex flex-col gap-4 bg-white dark:bg-zinc-900 p-4 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 dashboard-welcome">
            {businessProfileData && (
              <div className="w-full h-full flex flex-col gap-2">
                {/* <div className="w-full min-h-[290px] bg-gray-500 bg-opacity-50 rounded-xl" /> */}
                <div className="flex flex-col gap-2 mb-2">
                  <div className="flex flex-row gap-4 items-start mb-2 justify-between">
                    <div className="flex flex-col gap-2 dashboard-stats">
                      <span className="font-medium">
                        {businessProfileData.display_name}
                      </span>
                      <PlanLimitsCompact
                        currentOffices={offices.length || 0}
                        currentLocations={locations?.length || 0}
                        className="text-xs"
                      />
                    </div>
                    <Button
                      startContent={<FaPenToSquare />}
                      size="sm"
                      color="primary"
                      className="max-w-fit"
                      onClick={() =>
                        editBusinessProfileModalDisclosure.onOpen()
                      }
                    >
                      Редактировать
                    </Button>
                  </div>
                  <div className="flex flex-col p-4 gap-3 rounded-xl bg-zinc-100 dark:bg-zinc-950 border-2 border-foreground border-opacity-10 max-w-[512px] mb-2">
                    <span className="text-sm text-primary">
                      {businessProfileData.legal_address}
                    </span>
                    <span className="text-sm text-foreground-500">
                      ИНН: {businessProfileData.inn}
                    </span>
                    <span className="text-sm text-foreground-500">
                      ОГРН: {businessProfileData.ogrn}
                    </span>
                  </div>
                </div>
                <Divider className="mt-2 mb-2" />
                <div className="flex flex-col gap-2 mb-2">
                  <div className="flex flex-row gap-4 items-center mb-2 justify-between">
                    <span className="font-semibold">Локации</span>
                    <Button
                      startContent={<FaPlus />}
                      size="sm"
                      color="primary"
                      variant="solid"
                      className="max-w-fit"
                      onClick={() => createLocationModalDisclosure.onOpen()}
                    >
                      Создать
                    </Button>
                  </div>
                  {locations && locations.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 2xl:grid-cols-5 gap-3 items-center mt-2">
                      {locations.map((location, index) => (
                        <Link
                          href={`/dashboard/location/${location.id}`}
                          className="flex flex-col h-full bg-background border-2 border-default-200 rounded-xl cursor-pointer select-none text-foreground"
                          key={index}
                        >
                          <div className="flex flex-col gap-3 p-4">
                            <span className="text-medium font-bold">
                              {location.display_name}
                            </span>
                            <div className="flex flex-row gap-2 items-center">
                              <FaCity />
                              <span className="text-sm">
                                {location.city_display_name}
                              </span>
                            </div>
                            <div className="flex flex-row gap-2 items-center">
                              <FaMapPin />
                              <span className="text-sm">
                                {location.address}
                              </span>
                            </div>
                            {location.district && (
                              <div className="flex flex-row gap-2 items-center">
                                <FaBuilding />
                                <span className="text-sm">
                                  {location.district}
                                </span>
                              </div>
                            )}
                            {location.metro_station && (
                              <div className="flex flex-row gap-2 items-center">
                                <FaTrainSubway />
                                <span className="text-sm">
                                  {location.metro_station}
                                </span>
                              </div>
                            )}
                            <div className="flex flex-row gap-2 items-center">
                              <FaRuler />
                              <span className="text-sm">
                                {location.total_area} м2
                              </span>
                            </div>
                            <div className="flex flex-row gap-2 items-center">
                              <FaTag />
                              <span className="text-sm">
                                {formatCurrency(
                                  summaryLocations.find(
                                    (loc) => loc.locationId == location.id,
                                  )?.totalPrice,
                                )}{" "}
                                р.
                              </span>
                            </div>
                            <div className="flex flex-row gap-2 items-center">
                              <FaSquare />
                              <span className="text-sm">
                                {formatCurrency(
                                  summaryLocations.find(
                                    (loc) => loc.locationId == location.id,
                                  )?.pricePerSqm,
                                )}{" "}
                                р. / м².
                              </span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                  {locations?.length == 0 && (
                    <span>
                      Похоже, в вашем бизнес-профиле ещё нет ни одной локации.
                      Создайте первую!
                    </span>
                  )}
                </div>
              </div>
            )}
            {!businessProfileData && (
              <span>Идёт загрузка данных, подождите</span>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
